export const SUITE_PREFERENCES_METADATA_KEY = "tribunaleaPreferences";

const PREFERENCE_SESSION_COOKIE_KEY = "tribunalea_suite_preference_session";
const SUITE_PRODUCT_IDS = new Set(["instrumenta", "lexia", "praxis"]);

function normalizeWorkspace(value) {
  if (value?.type === "personal") {
    return { type: "personal", organizationId: null };
  }
  if (
    value?.type === "organization"
    && typeof value.organizationId === "string"
    && value.organizationId.trim()
  ) {
    return {
      type: "organization",
      organizationId: value.organizationId.trim(),
    };
  }
  return null;
}

function normalizeProduct(value) {
  return SUITE_PRODUCT_IDS.has(value) ? value : null;
}

export function normalizeSuitePreferences(value) {
  const input = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
  return {
    version: 1,
    workspace: normalizeWorkspace(input.workspace),
    lastProduct: normalizeProduct(input.lastProduct),
  };
}

export function mergeSuitePreferences(current, patch) {
  const next = normalizeSuitePreferences(current);
  if (Object.hasOwn(patch || {}, "workspace")) {
    next.workspace = normalizeWorkspace(patch.workspace);
  }
  if (Object.hasOwn(patch || {}, "lastProduct")) {
    next.lastProduct = normalizeProduct(patch.lastProduct);
  }
  return next;
}

export function workspacePreference(organizationId) {
  return organizationId
    ? { type: "organization", organizationId }
    : { type: "personal", organizationId: null };
}

export function sameSuitePreferences(left, right) {
  const first = normalizeSuitePreferences(left);
  const second = normalizeSuitePreferences(right);
  return first.lastProduct === second.lastProduct
    && first.workspace?.type === second.workspace?.type
    && first.workspace?.organizationId === second.workspace?.organizationId;
}

function sessionFingerprint(sessionId) {
  let hash = 2166136261;
  for (const character of String(sessionId || "")) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function readCookie(cookie, key) {
  const prefix = `${key}=`;
  const entry = String(cookie || "")
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!entry) return null;
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return null;
  }
}

export function hasRestoredPreferenceSession(sessionId, {
  cookie = typeof document !== "undefined" ? document.cookie : "",
} = {}) {
  if (!sessionId) return false;
  return readCookie(cookie, PREFERENCE_SESSION_COOKIE_KEY)
    === sessionFingerprint(sessionId);
}

export function markPreferenceSessionRestored(sessionId, {
  documentObject = typeof document !== "undefined" ? document : null,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  protocol = typeof window !== "undefined" ? window.location.protocol : "",
} = {}) {
  if (!sessionId || !documentObject) return;
  const sharedDomain = hostname === "tribunalea.com"
    || hostname.endsWith(".tribunalea.com")
    ? "; Domain=.tribunalea.com"
    : "";
  const secure = protocol === "https:" ? "; Secure" : "";
  documentObject.cookie = `${PREFERENCE_SESSION_COOKIE_KEY}=${encodeURIComponent(
    sessionFingerprint(sessionId),
  )}; Path=/; SameSite=Lax${secure}${sharedDomain}`;
}
